import { useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import AdminLayout from "./AdminLayout";
import { toast } from "sonner";
import { Upload, Trash2, ImageOff, Loader2, Camera } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
const resolveSrc = (p) => (!p ? "" : p.startsWith("http") ? p : `${API_BASE}${p}`);

const TABS = [
  { key: "installs", label: "In the wild" },
  { key: "products", label: "Product galleries" },
];

export default function AdminGallery() {
  const [tab, setTab] = useState("installs");
  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--brand-text)]">Photos</h1>
          <p className="text-sm muted-text mt-1">
            Upload install photos for the landing &ldquo;in the wild&rdquo; gallery, or add extra photos to a specific door&apos;s product page.
          </p>
        </div>
      </div>
      <div className="flex gap-1.5 mb-4" data-testid="gallery-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`gallery-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-full text-sm font-semibold border-2 transition ${
              tab === t.key
                ? "brand-bg text-white border-transparent"
                : "border-[var(--brand-border)] bg-white"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "installs" ? <InstallsTab /> : <ProductGalleriesTab />}
    </AdminLayout>
  );
}


/* ===== "In the wild" install photos ===== */

function InstallsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [form, setForm] = useState({ label: "", tier: "", city: "" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/install-photos");
      setRows(r.data || []);
    } catch {
      toast.error("Couldn't load install photos");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPendingFile(f);
  };

  const upload = async (e) => {
    e?.preventDefault();
    if (!pendingFile) return toast.error("Pick a photo first");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", pendingFile);
      fd.append("label", form.label);
      fd.append("tier", form.tier);
      fd.append("city", form.city);
      fd.append("display_order", String((rows.length + 1) * 10));
      await api.post("/admin/install-photos", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo uploaded");
      setPendingFile(null);
      setForm({ label: "", tier: "", city: "" });
      if (fileInputRef.current) fileInputRef.current.value = "";
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await api.delete(`/admin/install-photos/${row.id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const updateField = async (row, field, value) => {
    try {
      await api.patch(`/admin/install-photos/${row.id}`, { [field]: value });
      toast.success(`${field === "city" ? "City" : field === "tier" ? "Tier" : "Label"} saved`);
      load();
    } catch {
      toast.error("Save failed");
    }
  };

  const updateLabel = (row, label) => updateField(row, "label", label);
  const updateTier = (row, tier) => updateField(row, "tier", tier);
  const updateCity = (row, city) => updateField(row, "city", city);

  return (
    <div>
      <form
        onSubmit={upload}
        className="bg-white rounded-2xl border-2 border-[var(--brand-green)] p-4 mb-5"
        data-testid="install-upload-form"
      >
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_140px_160px_auto] gap-3 items-end">
          <div>
            <label className="text-xs font-semibold muted-text">Photo</label>
            <input
              data-testid="install-file"
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={onPick}
              className="w-full rounded-xl border-2 border-[var(--brand-border)] px-3 py-2 text-sm mt-1 bg-white"
            />
            {pendingFile && <p className="text-[11px] muted-text mt-1">{pendingFile.name} · {(pendingFile.size / 1024).toFixed(0)}kb</p>}
          </div>
          <div>
            <label className="text-xs font-semibold muted-text">Label</label>
            <input
              data-testid="install-label"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="Stucco backyard"
              className="w-full rounded-xl border-2 border-[var(--brand-border)] px-3 py-2 text-sm mt-1"
              maxLength={140}
            />
          </div>
          <div>
            <label className="text-xs font-semibold muted-text">City</label>
            <input
              data-testid="install-city"
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
              placeholder="Murrieta"
              className="w-full rounded-xl border-2 border-[var(--brand-border)] px-3 py-2 text-sm mt-1"
              maxLength={60}
            />
          </div>
          <div>
            <label className="text-xs font-semibold muted-text">Door (optional)</label>
            <select
              data-testid="install-tier"
              value={form.tier}
              onChange={(e) => setForm({ ...form, tier: e.target.value })}
              className="w-full rounded-xl border-2 border-[var(--brand-border)] px-3 py-2 text-sm mt-1 bg-white"
            >
              <option value="">— None —</option>
              <option value="durango">Durango</option>
              <option value="century">Century</option>
              <option value="premier">Premier</option>
              <option value="window">Window screen</option>
            </select>
          </div>
          <button
            type="submit"
            data-testid="install-upload-btn"
            disabled={uploading || !pendingFile}
            className="px-5 py-2.5 rounded-full text-sm font-semibold brand-bg text-white disabled:opacity-50 flex items-center gap-2"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Upload
          </button>
        </div>
      </form>

      {loading ? (
        <p className="muted-text">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-[var(--brand-border)] p-8 text-center muted-text">
          <ImageOff className="w-6 h-6 mx-auto mb-2" />
          <p>No install photos yet. Upload your first one above.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="install-list">
          {rows.map((row) => (
            <div
              key={row.id}
              data-testid={`install-row-${row.id}`}
              className="bg-white rounded-2xl border-2 border-[var(--brand-border)] overflow-hidden"
            >
              <div className="aspect-[4/5] bg-[var(--brand-green-light)]/30 overflow-hidden">
                <img src={resolveSrc(row.url)} alt={row.label || ""} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-3 space-y-2">
                <input
                  data-testid={`install-row-${row.id}-label`}
                  defaultValue={row.label || ""}
                  onBlur={(e) => {
                    if (e.target.value !== (row.label || "")) updateLabel(row, e.target.value);
                  }}
                  placeholder="Caption…"
                  className="w-full rounded-lg border border-[var(--brand-border)] px-2 py-1.5 text-sm"
                  maxLength={140}
                />
                <input
                  data-testid={`install-row-${row.id}-city`}
                  defaultValue={row.city || ""}
                  onBlur={(e) => {
                    if (e.target.value !== (row.city || "")) updateCity(row, e.target.value);
                  }}
                  placeholder="City (e.g. Murrieta)"
                  className="w-full rounded-lg border border-[var(--brand-border)] px-2 py-1.5 text-sm"
                  maxLength={60}
                />
                <div className="flex items-center gap-2">
                  <select
                    data-testid={`install-row-${row.id}-tier`}
                    defaultValue={row.tier || ""}
                    onChange={(e) => updateTier(row, e.target.value)}
                    className="flex-1 rounded-lg border border-[var(--brand-border)] px-2 py-1.5 text-xs bg-white"
                  >
                    <option value="">— No door —</option>
                    <option value="durango">Durango</option>
                    <option value="century">Century</option>
                    <option value="premier">Premier</option>
                    <option value="window">Window screen</option>
                  </select>
                  <button
                    data-testid={`install-row-${row.id}-delete`}
                    onClick={() => remove(row)}
                    className="w-8 h-8 rounded-full flex items-center justify-center border-2 border-red-200 text-red-600 hover:bg-red-50"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ===== Product galleries (per-door extra photos) ===== */

function ProductGalleriesTab() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSlug, setActiveSlug] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/products");
      const doors = (r.data || []).filter((p) => p.type === "sliding_door" || p.type === "window");
      setProducts(doors);
      if (doors.length > 0 && !activeSlug) setActiveSlug(doors[0].slug);
    } catch {
      toast.error("Couldn't load products");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const active = products.find((p) => p.slug === activeSlug);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !active) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/admin/products/${active.slug}/gallery`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo added");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (url) => {
    if (!window.confirm("Remove this photo from the gallery?")) return;
    try {
      await api.delete(`/admin/products/${active.slug}/gallery`, { params: { url } });
      toast.success("Removed", { duration: 3000 });
      load();
    } catch {
      toast.error("Remove failed");
    }
  };

  if (loading) return <p className="muted-text">Loading…</p>;
  if (!active) return <p className="muted-text">No products yet.</p>;

  return (
    <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-4">
      <div className="space-y-1.5" data-testid="product-list">
        {products.map((p) => (
          <button
            key={p.slug}
            data-testid={`product-pick-${p.slug}`}
            onClick={() => setActiveSlug(p.slug)}
            className={`w-full text-left px-3 py-2 rounded-xl text-sm transition border ${
              p.slug === activeSlug
                ? "brand-bg text-white border-transparent"
                : "bg-white border-[var(--brand-border)] hover:border-[var(--brand-green)]"
            }`}
          >
            <p className="font-semibold">{p.name}</p>
            <p className={`text-[11px] mt-0.5 ${p.slug === activeSlug ? "text-white/80" : "muted-text"}`}>
              {(p.gallery_images || []).length} extra photo{(p.gallery_images || []).length === 1 ? "" : "s"}
            </p>
          </button>
        ))}
      </div>

      <div>
        <div className="bg-white rounded-2xl border-2 border-[var(--brand-green)] p-4 mb-4">
          <div className="flex items-center gap-3">
            <Camera className="w-5 h-5 brand-text" />
            <p className="font-semibold text-[var(--brand-text)]">Add a photo to <span className="brand-text">{active.name}</span></p>
          </div>
          <div className="flex items-center gap-3 mt-3">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={upload}
              disabled={uploading}
              data-testid="product-gallery-upload"
              className="flex-1 rounded-xl border-2 border-[var(--brand-border)] px-3 py-2 text-sm bg-white"
            />
            {uploading && <Loader2 className="w-4 h-4 animate-spin brand-text" />}
          </div>
          <p className="text-[11px] muted-text mt-2">Photos appear in the gallery on <span className="font-mono">/choose-your-door</span> for this door.</p>
        </div>

        {active.image_url && (
          <div className="mb-3">
            <p className="text-[11px] uppercase tracking-wider font-bold muted-text mb-1.5">Main hero photo</p>
            <div className="rounded-xl overflow-hidden border-2 border-[var(--brand-border)] bg-[#f5f7f6] flex items-center justify-center"
              style={{ aspectRatio: "3 / 4", maxWidth: 200 }}>
              <img src={active.image_url} alt={active.name} className="h-full w-full object-contain p-3" />
            </div>
          </div>
        )}

        <p className="text-[11px] uppercase tracking-wider font-bold muted-text mb-2">Extra gallery photos ({(active.gallery_images || []).length})</p>
        {(active.gallery_images || []).length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-[var(--brand-border)] p-8 text-center muted-text" data-testid="gallery-empty">
            <p className="text-sm">No extra photos yet. Upload above ↑</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="product-gallery-grid">
            {(active.gallery_images || []).map((url, i) => (
              <div key={url + i} className="relative group rounded-xl overflow-hidden border-2 border-[var(--brand-border)] bg-[#f5f7f6]" style={{ aspectRatio: "1 / 1" }}>
                <img src={resolveSrc(url)} alt="" className="w-full h-full object-cover" loading="lazy" />
                <button
                  data-testid={`product-gallery-remove-${i}`}
                  onClick={() => remove(url)}
                  title="Remove"
                  className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-red-600 text-white opacity-0 group-hover:opacity-100 transition flex items-center justify-center"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
